
import {zip as observableZip,  Observable, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { LoggedInUserService } from '../../auth/logged.in.user';
import { CategoryAndLocationHelpers } from '../../helpers';
import { GetExtraAlertsDto, UpdateExtraAlertDto } from '../../models/account/account.models';
import { SelectableCategory } from '../../models/categories/category.models';
import { LocationsByRegionDto, SelectableLocation, SelectableRegion } from '../../models/locations/location.models';
import { AccountService } from '../../services/account.service';
import { CategoryService } from '../../services/category.service';
import { LocationService } from '../../services/location.service';

@Component({
    selector: 'edit-extra-alert',
    template: require('./edit.extra.alert.component.html'),
    styles: [require('./personal.account.shared.css'), require('./tender.alert.profile.component.css'), require('./edit.extra.alert.component.css')]
})
export class EditExtraAlertComponent implements OnInit, OnDestroy {
    categories: Array<SelectableCategory>;
    locationsByRegion: LocationsByRegionDto;
    extraAlertDetails: GetExtraAlertsDto;

    showCatLocError = false;
    tooManyCategoriesSelected = false;

    @BlockUI('categories-and-locations-block') private catAndLocBlock : NgBlockUI;

    categoryFilter: string;

    subscribed: boolean;
    routeParamsSub: Subscription;

    constructor(private readonly categoryService: CategoryService,
        private readonly locationService: LocationService,
        private readonly accountService: AccountService,
        private readonly helpers: CategoryAndLocationHelpers,
        loggedInUserService: LoggedInUserService,
        private readonly router: Router,
        private route: ActivatedRoute) {

        localStorage.setItem('tooManyCategoriesSelected', 'false');
        localStorage.setItem('categoriesSelected', '0');

        var user = loggedInUserService.getCurrentUser();
        this.subscribed = !user.canAdHoc('notSubscribed');
    }

    ngOnInit() {
        document.body.classList.add('personal-account-fit');

        this.loadAlertDetails();
    }

    ngOnDestroy() {
        document.body.classList.remove('personal-account-fit');
    }

    private loadAlertDetails() {
        var categoryObservable = this.categoryService.getActiveCategories();
        var locationObservable = this.locationService.getActiveLocations();

        if (this.routeParamsSub) this.routeParamsSub.unsubscribe();
        this.routeParamsSub = this.route.params.subscribe(params => {
            const id = +params['id'];

            var extraAlertObservable = this.accountService.getExtraAlertDetails(id);

            this.catAndLocBlock.start();

            observableZip(categoryObservable,
                locationObservable,
                extraAlertObservable,
                (categories: Array<SelectableCategory>,
                    locations: LocationsByRegionDto,
                    alertDetails: GetExtraAlertsDto) => ({ categories, locations, alertDetails }))
                .subscribe(results => {
                    this.categories = results.categories;
                    this.locationsByRegion = results.locations;
                    this.extraAlertDetails = results.alertDetails;

                    this.helpers.setSelectedCategories(this.categories, this.extraAlertDetails.categoryIds);
                    this.helpers.setSelectedLocations(this.locationsByRegion, this.extraAlertDetails.locationIds);

                    this.categoriesChanged();
                    this.catAndLocBlock.stop();
                });
        });

    }

    async filterCategories() {
        if (this.categoryFilter && this.categoryFilter.length >= 3) {
            var filter = this.categoryFilter.toLowerCase();
            this.categories.forEach(async (category: SelectableCategory) => {
                await this.checkIfCategoryOrChildrenMatch(category, filter);
            });
        } else {
            this.categories.forEach((category: SelectableCategory) => { this.showCategoryAndChildren(category) });
        }
    }

    async checkIfCategoryOrChildrenMatch(category: SelectableCategory, filter: string, parent?: SelectableCategory) {
        category.show = category.name.toLowerCase().indexOf(filter) > -1 ||
            (category.keywords && category.keywords.indexOf(filter) > -1);

        if (parent && category.show) {
            parent.show = true;
            parent.isExpanded = true;
        }

        category.children.forEach(async (child: SelectableCategory) => {
            await this.checkIfCategoryOrChildrenMatch(child, filter, category);
        });
    }

    async showCategoryAndChildren(category: SelectableCategory) {
        category.show = true;

        category.children.forEach((child: SelectableCategory) => { this.showCategoryAndChildren(child) });
    }

    changeRegionSelected(region: SelectableRegion) {
        region.locations.forEach((location: SelectableLocation) => {
            location.isSelected = region.isSelected;
        });
    }

    changeLocationSelected(location: SelectableLocation, region?: SelectableRegion) {
        if (!region) {
            return;
        }

        if (region.locations.every((l: SelectableLocation) => { return l.isSelected })) {
            region.isSelected = true;
        } else if (region.locations.every((l: SelectableLocation) => { return !l.isSelected })) {
            region.isSelected = false;
        }
    }

    categoriesChanged() {
        if (!this.categories) {
            return;
        }
        let categoriesSelectedCount = 0;
        this.categories.forEach((category: SelectableCategory) => {
            if (category.children.length == 0) {
                if (category.isSelected) {
                    categoriesSelectedCount++;
                }
            }
            else {
                category.children.forEach((child: SelectableCategory) => {
                    if (child.isSelected) {
                        categoriesSelectedCount++;
                    }
                });
            }
        });

        localStorage.setItem('categoriesSelected', categoriesSelectedCount.toString());

        if (categoriesSelectedCount == 29 && !this.subscribed) {
            this.tooManyCategoriesSelected = false;
            localStorage.setItem('tooManyCategoriesSelected', 'true');
        }
        else if (categoriesSelectedCount > 29 && !this.subscribed) {
            this.tooManyCategoriesSelected = true;
            localStorage.setItem('tooManyCategoriesSelected', 'true');
        }
        else {
            this.tooManyCategoriesSelected = false;
            localStorage.setItem('tooManyCategoriesSelected', 'false');
        }
    }

    saveExtraAlert() {
        this.catAndLocBlock.start();
        this.tooManyCategoriesSelected = false;

        var dto: UpdateExtraAlertDto = {
            id: this.extraAlertDetails.id,
            description: this.extraAlertDetails.description,
            supplierId: this.extraAlertDetails.supplierId,
            categoryIds: [],
            locationIds: [],
            includeAwards: this.extraAlertDetails.includeAwards
        };

        var checkCategorySelection = (category: SelectableCategory) => {
            if (category.isSelected) {
                dto.categoryIds.push(category.id);
            }

            category.children.forEach((child: SelectableCategory) => {
                checkCategorySelection(child);
            });
        }

        this.categories.forEach((category: SelectableCategory) => { checkCategorySelection(category); });

        var checkLocationSelection = (location: SelectableLocation) => {
            if (location.isSelected) {
                dto.locationIds.push(location.id);
            }
        }

        this.locationsByRegion.regions.forEach((r: SelectableRegion) => {
            r.locations.forEach((l: SelectableLocation) => { checkLocationSelection(l); });
        });

        this.locationsByRegion.locationsWithoutRegion.forEach((l: SelectableLocation) => {
            checkLocationSelection(l);
        });

        if (dto.categoryIds.length === 0 || dto.locationIds.length === 0) {
            this.showCatLocError = true;
            this.catAndLocBlock.stop();
            return;
        }

        this.showCatLocError = false;

        this.accountService.updateExtraAlert(dto).subscribe(() => {
            this.catAndLocBlock.stop();
            this.router.navigateByUrl('/account/extraAlerts');
        }, () => {
            this.catAndLocBlock.stop();
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/account.management/edit.extra.alert.component.ts